/// Firebase Auth wrapper.
///
/// Boot flow:
///   1. First load on a device  →  signInAnonymously() — gives the
///      browser a Firebase UID + Firestore profile so the player can
///      track scores without an account.
///   2. Player taps Sign in with Google  →  linkWithPopup() upgrades
///      the existing anonymous user *in place*. Same UID, same
///      Firestore doc, just with email + displayName attached and
///      `authProvider: 'google'` recorded.
///   3. Subsequent loads on the same device  →  Firebase persists
///      the session so currentUser is restored without prompting.

import {
  EmailAuthProvider,
  GoogleAuthProvider,
  OAuthProvider,
  createUserWithEmailAndPassword,
  fetchSignInMethodsForEmail,
  linkWithCredential,
  linkWithPopup,
  onAuthStateChanged,
  sendEmailVerification,
  sendPasswordResetEmail,
  signInAnonymously,
  signInWithCredential,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut as firebaseSignOut,
  updateProfile,
  type AuthProvider as FirebaseAuthProvider,
  type User,
} from 'firebase/auth'
import { firebaseAuth } from '../firebase/app'
import type { AuthProvider } from '../data/userProfile'

export async function ensureSignedIn(): Promise<User> {
  // Wait for Firebase to restore any persisted session before
  // deciding to sign in anonymously. On a cold page load (e.g.,
  // returning from a Stripe Checkout redirect) `currentUser` is
  // null synchronously — the persistence layer rehydrates from
  // IndexedDB asynchronously and the first onAuthStateChanged
  // callback fires once it settles. Without waiting, we'd
  // create a new anonymous user and clobber the player's
  // Google session.
  return new Promise<User>((resolve, reject) => {
    const unsub = onAuthStateChanged(
      firebaseAuth,
      async (user) => {
        unsub()
        if (user) {
          resolve(user)
          return
        }
        try {
          const cred = await signInAnonymously(firebaseAuth)
          resolve(cred.user)
        } catch (e) {
          reject(e)
        }
      },
      reject,
    )
  })
}

export function onUserChange(cb: (user: User | null) => void): () => void {
  return onAuthStateChanged(firebaseAuth, cb)
}

export function currentUser(): User | null {
  return firebaseAuth.currentUser
}

/// Outcome of `signInWithGoogle`. The `existing-account` variant
/// signals that we discarded the freshly-created anonymous user and
/// switched to the existing Google-linked Firebase UID — the caller
/// must reload its profile from Firestore for the new UID since the
/// stale in-memory profile belonged to the abandoned anonymous user.
export interface GoogleSignInResult {
  user: User
  /// 'linked'    = anonymous → Google upgrade in place (UID kept)
  /// 'existing-account' = found an existing Google-linked Firebase
  ///                      user, signed into it. UID changed.
  /// 'fresh'     = no prior user; signed in via popup directly.
  /// 'no-op'     = already signed in with a real provider; nothing
  ///               to do.
  kind: 'linked' | 'existing-account' | 'fresh' | 'no-op'
}

/// Sign in with Google.
///
/// Branches:
///   1. Anonymous current user, this Google account is unbound:
///      `linkWithPopup` upgrades in place. Same UID, same profile.
///   2. Anonymous current user, this Google account is already
///      bound to a different Firebase UID
///      (auth/credential-already-in-use): we extract the Google
///      credential from the error and `signInWithCredential` to
///      that existing UID. The freshly-created anonymous user is
///      displaced — its data is discarded, but in practice this is
///      the desired outcome: the player wants to use their real
///      account, not a guest session that's only minutes old.
///   3. No current user: plain `signInWithPopup`.
///   4. Already on a real provider: no-op, returns current user.
///
/// Throws on:
///   - auth/popup-closed-by-user / popup-blocked: user dismissed
///     the popup. Caller treats as a quiet no-op.
///   - auth/operation-not-allowed: the Google provider isn't
///     enabled in Firebase Console (one-time admin setup).
export async function signInWithGoogle(): Promise<GoogleSignInResult> {
  const provider = new GoogleAuthProvider()
  // Always request a basic profile + email so we can show the
  // player's name in the chrome.
  provider.addScope('email')
  provider.addScope('profile')
  const current = firebaseAuth.currentUser
  if (current && !current.isAnonymous) {
    return { user: current, kind: 'no-op' }
  }
  if (current && current.isAnonymous) {
    try {
      const cred = await linkWithPopup(current, provider as FirebaseAuthProvider)
      return { user: cred.user, kind: 'linked' }
    } catch (err) {
      if ((err as { code?: string }).code === 'auth/credential-already-in-use') {
        // The Google credential is bound to a different Firebase
        // UID. Sign in to that account directly — the anonymous
        // user is automatically displaced. The caller is
        // responsible for reloading its Firestore profile for the
        // new UID.
        const credential = GoogleAuthProvider.credentialFromError(
          err as { customData?: unknown } as never,
        )
        if (!credential) throw err
        const result = await signInWithCredential(firebaseAuth, credential)
        return { user: result.user, kind: 'existing-account' }
      }
      throw err
    }
  }
  const cred = await signInWithPopup(firebaseAuth, provider)
  return { user: cred.user, kind: 'fresh' }
}

/// Sign in with Apple.
///
/// Same branch logic as `signInWithGoogle` — see that function's
/// header comment for the four cases (link / existing-account /
/// fresh / no-op). Apple returns the credential as
/// `OAuthCredential` rather than `GoogleAuthCredential`, so we use
/// `OAuthProvider.credentialFromError` to recover it from the
/// `auth/credential-already-in-use` path.
///
/// Setup gates (one-time, not in code):
///   1. Apple Developer → Identifiers → Service IDs: create one
///      for the web (e.g., `com.liveflatout.flatoutatc.web`) with
///      Sign in with Apple enabled, domain `flatoutatc.com`,
///      return URL `https://flatoutatc.com/__/auth/handler`
///      (and the default firebaseapp.com handler as a fallback).
///   2. Apple Developer → Keys: create a key with Sign in with
///      Apple enabled, download the .p8.
///   3. Firebase Console → Authentication → Sign-in method →
///      Apple: enable, paste Service ID + Team ID + Key ID +
///      .p8 contents.
///   4. Firebase Console → Authentication → Settings → Authorized
///      domains: confirm `flatoutatc.com` (and any other
///      production domain) is listed. Already in for Google.
export async function signInWithApple(): Promise<GoogleSignInResult> {
  const provider = new OAuthProvider('apple.com')
  provider.addScope('email')
  provider.addScope('name')
  const current = firebaseAuth.currentUser
  if (current && !current.isAnonymous) {
    return { user: current, kind: 'no-op' }
  }
  if (current && current.isAnonymous) {
    try {
      const cred = await linkWithPopup(current, provider as FirebaseAuthProvider)
      return { user: cred.user, kind: 'linked' }
    } catch (err) {
      if ((err as { code?: string }).code === 'auth/credential-already-in-use') {
        const credential = OAuthProvider.credentialFromError(
          err as { customData?: unknown } as never,
        )
        if (!credential) throw err
        const result = await signInWithCredential(firebaseAuth, credential)
        return { user: result.user, kind: 'existing-account' }
      }
      throw err
    }
  }
  const cred = await signInWithPopup(firebaseAuth, provider)
  return { user: cred.user, kind: 'fresh' }
}

/// Sign out and immediately re-sign-in anonymously so the home
/// screen never enters a no-user state. The old Firestore doc
/// stays put under its original UID; the new anonymous user gets a
/// fresh empty profile.
export async function signOutToAnonymous(): Promise<User> {
  await firebaseSignOut(firebaseAuth)
  const cred = await signInAnonymously(firebaseAuth)
  return cred.user
}

/// Map a Firebase User to our internal AuthProvider tag. Anonymous
/// users are tagged as such; otherwise we read the first
/// providerData entry to figure out whether they upgraded via
/// Google, Apple, or email/password.
export function authProviderOf(user: User): AuthProvider {
  if (user.isAnonymous) return 'anonymous'
  const id = user.providerData[0]?.providerId
  if (id === 'google.com') return 'google'
  if (id === 'apple.com') return 'apple'
  if (id === 'password') return 'email'
  // Shouldn't happen with the providers we support today; fall
  // through to the closest tag.
  return 'anonymous'
}

/// Email/password — sign-up flow.
///
/// Branches:
///   1. Anonymous current user, email is unbound:
///      `linkWithCredential` upgrades in place. Same UID, same
///      profile. After link, we send a verification email and
///      optionally set displayName so the chrome / leaderboards
///      have a friendly handle.
///   2. Anonymous current user, email is already bound to a
///      different Firebase UID
///      (auth/credential-already-in-use): falls through to plain
///      `createUserWithEmailAndPassword`, replacing the anonymous
///      user — same trade-off as the Google branch makes.
///   3. No current user: plain `createUserWithEmailAndPassword`.
///   4. Already on a real provider: throws — UI should guide the
///      user to sign out first.
export interface EmailSignUpResult {
  user: User
  /// true ⟹ verification email was sent successfully and the user
  /// should check their inbox before fully trusting the account.
  verificationSent: boolean
  /// 'linked' = anonymous → email upgrade in place (UID kept).
  /// 'fresh'  = no anonymous user; new account created directly.
  kind: 'linked' | 'fresh'
}

export async function signUpWithEmail(args: {
  email: string
  password: string
  displayName?: string
}): Promise<EmailSignUpResult> {
  const { email, password, displayName } = args
  const current = firebaseAuth.currentUser

  let user: User
  let kind: EmailSignUpResult['kind']
  if (current && current.isAnonymous) {
    const credential = EmailAuthProvider.credential(email, password)
    try {
      const result = await linkWithCredential(current, credential)
      user = result.user
      kind = 'linked'
    } catch (err) {
      if ((err as { code?: string }).code === 'auth/credential-already-in-use') {
        // Fall through to a fresh sign-up. The anonymous user is
        // displaced — its data stays under the old UID.
        const result = await createUserWithEmailAndPassword(
          firebaseAuth,
          email,
          password,
        )
        user = result.user
        kind = 'fresh'
      } else {
        throw err
      }
    }
  } else if (!current) {
    const result = await createUserWithEmailAndPassword(
      firebaseAuth,
      email,
      password,
    )
    user = result.user
    kind = 'fresh'
  } else {
    throw new Error('Already signed in — sign out before creating a new account.')
  }

  if (displayName && displayName.trim().length > 0) {
    try {
      await updateProfile(user, { displayName: displayName.trim() })
    } catch (e) {
      // Non-fatal — the account exists, just without a friendly
      // display name. Caller can retry later.
      console.warn('updateProfile(displayName) failed', e)
    }
  }

  let verificationSent = false
  try {
    await sendEmailVerification(user)
    verificationSent = true
  } catch (e) {
    console.warn('sendEmailVerification failed', e)
  }

  return { user, verificationSent, kind }
}

/// Email/password — sign-in flow. Replaces the current user
/// (anonymous or otherwise) with the email-authenticated one.
/// Caller is responsible for reloading the Firestore profile for
/// the resulting UID — same contract as the Google
/// `existing-account` branch.
export async function signInWithEmail(args: {
  email: string
  password: string
}): Promise<User> {
  const result = await signInWithEmailAndPassword(
    firebaseAuth,
    args.email,
    args.password,
  )
  return result.user
}

/// Send a password-reset email. Firebase responds with a generic
/// "ok" to most invalid emails to prevent account enumeration; we
/// surface specific errors when we get them anyway.
export async function sendPasswordReset(email: string): Promise<void> {
  await sendPasswordResetEmail(firebaseAuth, email)
}

/// Re-send the verification email to the currently signed-in user.
export async function resendVerificationEmail(): Promise<void> {
  if (!firebaseAuth.currentUser) {
    throw new Error('No signed-in user to verify.')
  }
  await sendEmailVerification(firebaseAuth.currentUser)
}

/// Short user-facing label for the current sign-in state.
/// Displayed on the home screen.
export function userBadgeLabel(user: User | null): string {
  if (!user) return 'Signing in…'
  if (user.isAnonymous) return 'Guest'
  if (user.displayName) return user.displayName
  if (user.email) return user.email
  return 'Signed in'
}

/// Build a player-readable message for the Firebase email-collision
/// error path. Asks Firebase which providers ARE linked to the
/// colliding email and recommends using one. Used by both the
/// Google and Apple sign-in code paths — same flow, mirrored
/// errors. Returns a generic fallback if the lookup fails.
export async function describeEmailCollision(err: unknown): Promise<string> {
  const code = (err as { code?: string }).code ?? ''
  const email = (err as { customData?: { email?: string } }).customData?.email
  if (!email) {
    return code === 'auth/email-already-in-use' ||
      code === 'auth/account-exists-with-different-credential'
      ? 'This email is already signed in with another provider. Please use that one to sign in.'
      : 'Sign-in failed.'
  }
  try {
    const methods = await fetchSignInMethodsForEmail(firebaseAuth, email)
    const friendly = methods
      .map((m) => {
        if (m === 'google.com') return 'Google'
        if (m === 'apple.com') return 'Apple'
        if (m === 'password') return 'email + password'
        return m
      })
      .filter((s) => s.length > 0)
    if (friendly.length === 0) {
      return `${email} is already in use. Try a different account.`
    }
    return `${email} is already signed in with ${friendly.join(' or ')}. Please use that to sign in.`
  } catch {
    return `${email} is already in use. Please sign in with the original provider.`
  }
}

/// Error codes that mean "this email collides with an existing
/// Firebase account on a different provider." Both signed-in paths
/// (Google + Apple) need the same handling.
export function isEmailCollisionError(err: unknown): boolean {
  const code = (err as { code?: string }).code ?? ''
  return (
    code === 'auth/email-already-in-use' ||
    code === 'auth/account-exists-with-different-credential'
  )
}
